import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  INCORPORATION_ACKNOWLEDGEMENT,
  INCORPORATION_TERMS,
  type PrimordialShipOption,
} from './primordial-incorporation';

/**
 * Incorporar um Coração Primordial numa nave (gate V28F.C).
 *
 * ## Dois passos, e o segundo é um modal próprio
 *
 * Escolher a nave e confirmar a irreversibilidade são duas decisões diferentes,
 * e juntá-las num ecrã só faria a segunda acontecer por inércia. O primeiro
 * passo mostra o que cada nave ganha; o segundo diz o que se perde e obriga a
 * dizer que sim.
 *
 * ## Nunca a nave equipada por atalho
 *
 * A operação é IRREVERSÍVEL e o coração é gasto. Um gesto irreversível que
 * adivinha o alvo é um gesto que um dia acerta no alvo errado, e não há rota
 * inversa para desfazer esse dia. A nave equipada aparece primeiro na lista
 * porque é a que o jogador tem em mente - e continua a ser preciso escolhê-la.
 *
 * ## Uma recusa não fecha nada
 *
 * `ship_reserved` é o caso normal, não o excepcional: outra aba pode ter
 * anunciado a nave entre o clique e o pedido. O modal fica aberto, a razão
 * aparece por palavras, a escolha não se perde, e a lista vem outra vez do
 * servidor - a mesma nave passa a aparecer desabilitada, com o motivo escrito.
 *
 * ## O que este componente NÃO faz
 *
 * Não faz pedidos, não gera chaves de idempotência e não sabe o que é uma
 * resposta HTTP. Recebe as naves já modeladas, chama `onIncorporate` uma vez e
 * desenha o que lhe disserem - a chave e o replay são de quem chama, que é onde
 * a intenção nasce.
 */

export type IncorporationStatus = 'idle' | 'busy' | 'done';

export interface PrimordialIncorporationDialogProps {
  /** O nome legível do Coração escolhido, para o jogador saber qual é. */
  readonly heartLabel: string;
  readonly ships: readonly PrimordialShipOption[];
  readonly status: IncorporationStatus;
  /** A frase da última recusa, ou `null`. Nunca um código, nunca um status. */
  readonly failure: string | null;
  readonly onIncorporate: (shipInstanceId: string) => void;
  readonly onClose: () => void;
  /**
   * Para onde o foco volta ao fechar, resolvido NO MOMENTO de fechar.
   *
   * Uma função e não um nó, porque o nó pode ter sido substituído entretanto:
   * uma recusa relê o inventário, a prateleira volta a desenhar-se, e o botão
   * que abriu a janela passa a ser outro elemento com a mesma identidade
   * lógica. Guardar a referência antiga devolveria o foco a um órfão - isto é,
   * a nada, e em silêncio.
   *
   * Opcional: sem ela, a janela devolve o foco a quem a abriu, enquanto esse
   * alguém ainda estiver no documento.
   */
  readonly returnFocus?: () => HTMLElement | null;
}

/** O que foi confirmado, fotografado ANTES do pedido. */
interface Confirmed {
  readonly shipInstanceId: string;
  readonly name: string;
  readonly before: string;
  readonly after: string;
  readonly bonusBefore: string;
  readonly bonusAfter: string;
}

export function PrimordialIncorporationDialog({
  heartLabel,
  ships,
  status,
  failure,
  onIncorporate,
  onClose,
  returnFocus,
}: PrimordialIncorporationDialogProps) {
  const [chosenId, setChosenId] = useState<string | null>(null);
  const [acknowledged, setAcknowledged] = useState(false);
  const [confirmed, setConfirmed] = useState<Confirmed | null>(null);
  const dialog = useRef<HTMLDivElement | null>(null);
  const titleId = useId();
  const ackId = useId();

  const chosen = chosenId ? (ships.find((ship) => ship.shipInstanceId === chosenId) ?? null) : null;
  const step: 'ship' | 'confirm' = chosen === null ? 'ship' : 'confirm';

  /**
   * E o foco VOLTA para onde estava, quando a janela fecha.
   *
   * Sem isto, fechar deixa `document.activeElement` no `<body>`: quem navega por
   * teclado recomeça no topo do documento, e quem usa um leitor de ecrã perde o
   * sítio onde estava a trabalhar - depois de ter carregado num botão a meio de
   * uma prateleira.
   *
   * `isConnected` é a condição, e não uma comodidade: numa incorporação com
   * sucesso o botão que abriu a janela DESAPARECE, porque o Coração que ele
   * nomeava foi gasto. Devolver o foco a um nó que já não está no documento não
   * faz nada em silêncio - a verificação é o que faz a diferença entre restaurar
   * e parecer restaurar.
   */
  const resolveReturnFocus = useRef(returnFocus);
  resolveReturnFocus.current = returnFocus;
  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null;
    return () => {
      /* Quem chama decide primeiro, e decide TARDE - só aqui, ao fechar. */
      const asked = resolveReturnFocus.current?.() ?? null;
      const target = asked?.isConnected ? asked : opener?.isConnected ? opener : null;
      target?.focus();
    };
  }, []);

  /**
   * O foco entra no diálogo, e volta a entrar a cada passo.
   *
   * Não só na montagem. Escolher uma nave DESMONTA o botão que foi carregado -
   * o passo muda para a confirmação - e o foco cai no `<body>`: quem usa um
   * leitor de ecrã perde o sítio, e o próprio laço do Tab deixa de funcionar,
   * porque ele decide a partir de `document.activeElement` estar dentro da
   * janela. A auditoria da folha de contacto apanhou isto exactamente aqui.
   */
  /*
   * `step` e `status` não são LIDOS aqui, e é de propósito: são o gatilho. A
   * regra vê uma `ref` no corpo e conclui que o efeito não depende de nada; o
   * que ele precisa é de correr outra vez sempre que o conteúdo do diálogo é
   * substituído.
   */
  // biome-ignore lint/correctness/useExhaustiveDependencies: as duas são o gatilho, não uma leitura
  useEffect(() => {
    dialog.current?.focus();
  }, [step, status]);

  const close = useCallback(() => {
    if (status === 'busy') {
      /* Um pedido em curso não se cancela: a escrita já pode ter acontecido. */
      return;
    }
    onClose();
  }, [onClose, status]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        close();
        return;
      }
      if (event.key !== 'Tab' || !dialog.current) {
        return;
      }
      /*
       * O teclado fica no modal.
       *
       * Sem isto o Tab sai para o inventário por trás - invisível para quem vê,
       * e completamente perdido para quem usa um leitor de ecrã. O checkbox
       * entra no laço: sem ele o botão final ficaria inalcançável por teclado,
       * porque é exactamente ele que o desbloqueia.
       */
      const focusable = [
        ...dialog.current.querySelectorAll<HTMLElement>(
          'button:not([disabled]), input:not([disabled])',
        ),
      ];
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (!first || !last) {
        return;
      }
      const active = document.activeElement;
      if (event.shiftKey && (active === first || active === dialog.current)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [close]);

  const eligible = ships.filter((ship) => ship.eligible);

  return createPortal(
    <div
      className="primordial-overlay"
      data-testid="primordial-overlay"
      /*
       * "Este portal É o inventário", declarado.
       *
       * O painel fecha-se com um `mousedown` na fase de CAPTURA sempre que o
       * alvo bruto cai fora dele - e um portal para `document.body` cai sempre
       * fora, diga a árvore do React o que disser. Sem esta marca, escolher uma
       * nave fechava o inventário inteiro por baixo da janela que o jogador
       * estava a usar. A roleta das skins já tinha aprendido isto; o marcador
       * existe para se aderir a ele, não para se editar aquela lista.
       */
      data-inventory-portal=""
    >
      {/*
        `tabIndex={-1}` num `role="dialog"` é o que o torna focável por programa
        sem o pôr na ordem do Tab.
      */}
      <div
        ref={dialog}
        className="primordial-dialog"
        data-testid="primordial-dialog"
        data-step={status === 'done' ? 'done' : step}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
      >
        {status === 'done' && confirmed ? (
          <SuccessPanel titleId={titleId} confirmed={confirmed} onClose={onClose} />
        ) : step === 'ship' ? (
          <ShipStep
            titleId={titleId}
            heartLabel={heartLabel}
            ships={ships}
            eligibleCount={eligible.length}
            failure={failure}
            onChoose={setChosenId}
            onClose={close}
          />
        ) : (
          <ConfirmStep
            titleId={titleId}
            ackId={ackId}
            ship={chosen as PrimordialShipOption}
            status={status}
            failure={failure}
            acknowledged={acknowledged}
            onAcknowledge={setAcknowledged}
            onBack={() => {
              setChosenId(null);
              setAcknowledged(false);
            }}
            onConfirm={() => {
              const ship = chosen as PrimordialShipOption;
              setConfirmed({
                shipInstanceId: ship.shipInstanceId,
                name: ship.name,
                before: ship.heartCount,
                after: ship.heartCountAfter,
                bonusBefore: ship.bonusPercent,
                bonusAfter: ship.bonusPercentAfter,
              });
              onIncorporate(ship.shipInstanceId);
            }}
          />
        )}
      </div>
    </div>,
    document.body,
  );
}

/* ------------------------------------------------------------- passo 1 -- */

function ShipStep({
  titleId,
  heartLabel,
  ships,
  eligibleCount,
  failure,
  onChoose,
  onClose,
}: {
  titleId: string;
  heartLabel: string;
  ships: readonly PrimordialShipOption[];
  eligibleCount: number;
  failure: string | null;
  onChoose: (id: string) => void;
  onClose: () => void;
}) {
  return (
    <>
      <h2 className="primordial-title" id={titleId}>
        ESCOLHA UMA NAVE
      </h2>
      <p className="primordial-lead" data-testid="primordial-heart-label">
        {heartLabel} será incorporado permanentemente na nave que escolher.
      </p>
      {failure ? (
        <p className="primordial-failure" role="alert" data-testid="primordial-failure">
          {failure}
        </p>
      ) : null}
      {ships.length === 0 ? (
        <p className="primordial-empty" data-testid="primordial-empty">
          Você não tem nenhuma nave no inventário.
        </p>
      ) : eligibleCount === 0 ? (
        <p className="primordial-empty" data-testid="primordial-empty">
          Nenhuma das suas naves pode receber um Coração agora.
        </p>
      ) : null}
      <ul className="primordial-ships" data-testid="primordial-ship-list">
        {ships.map((ship) => (
          <li key={ship.shipInstanceId}>
            <button
              type="button"
              className="primordial-ship"
              data-testid={`primordial-ship-${ship.shipInstanceId}`}
              data-eligible={ship.eligible ? 'true' : 'false'}
              data-equipped={ship.equipped ? 'true' : 'false'}
              disabled={!ship.eligible}
              onClick={() => onChoose(ship.shipInstanceId)}
            >
              <span className="primordial-ship-head">
                <span className="primordial-ship-name">{ship.name}</span>
                <span className="primordial-ship-level">nível {ship.level}</span>
              </span>
              <span className="primordial-ship-tags">
                {/*
                  Estado por PALAVRAS, nunca só por cor.
                  Um distintivo que só existisse como uma opacidade diferente
                  seria invisível para quem não distingue as duas.
                */}
                <span className="primordial-tag" data-tag="slot">
                  {ship.equipped ? 'Equipada' : 'Não equipada'}
                </span>
                {/*
                 * "Vinculada", e não "Soulbound".
                 *
                 * O jogo fala português com o jogador, e este era o único
                 * rótulo público em inglês. O campo continua a chamar-se
                 * `soulbound` no contrato, na base e no cofre - o que muda é a
                 * palavra que uma pessoa lê, não o nome que o código usa.
                 */}
                <span className="primordial-tag" data-tag="trade">
                  {ship.soulbound ? 'Vinculada' : ship.tradable ? 'Negociável' : 'Não negociável'}
                </span>
              </span>
              <span className="primordial-ship-change">
                <span data-testid={`primordial-ship-${ship.shipInstanceId}-hearts`}>
                  Corações: {ship.heartCount} → {ship.heartCountAfter}
                </span>
                <span data-testid={`primordial-ship-${ship.shipInstanceId}-bonus`}>
                  Bônus: +{ship.bonusPercent}% → +{ship.bonusPercentAfter}%
                </span>
              </span>
              {ship.blockText ? (
                <span
                  className="primordial-ship-block"
                  data-testid={`primordial-ship-${ship.shipInstanceId}-block`}
                >
                  {ship.blockText}
                </span>
              ) : null}
            </button>
          </li>
        ))}
      </ul>
      <div className="primordial-actions">
        <button
          type="button"
          className="primordial-button"
          data-weight="quiet"
          data-testid="primordial-cancel"
          onClick={onClose}
        >
          VOLTAR SEM INCORPORAR
        </button>
      </div>
    </>
  );
}

/* ------------------------------------------------------------- passo 2 -- */

function ConfirmStep({
  titleId,
  ackId,
  ship,
  status,
  failure,
  acknowledged,
  onAcknowledge,
  onBack,
  onConfirm,
}: {
  titleId: string;
  ackId: string;
  ship: PrimordialShipOption;
  status: IncorporationStatus;
  failure: string | null;
  acknowledged: boolean;
  onAcknowledge: (value: boolean) => void;
  onBack: () => void;
  onConfirm: () => void;
}) {
  const busy = status === 'busy';
  return (
    <>
      <h2 className="primordial-title" id={titleId}>
        INCORPORAR CORAÇÃO PRIMORDIAL
      </h2>
      <ul className="primordial-terms" data-testid="primordial-terms">
        {INCORPORATION_TERMS.map((term) => (
          <li key={term}>{term}</li>
        ))}
      </ul>
      <dl className="primordial-summary" data-testid="primordial-summary">
        <div>
          <dt>Nave</dt>
          <dd data-testid="primordial-summary-ship">
            {ship.name} · nível {ship.level} · {ship.equipped ? 'Equipada' : 'Não equipada'}
          </dd>
        </div>
        <div>
          <dt>Corações</dt>
          <dd data-testid="primordial-summary-hearts">
            {ship.heartCount} → {ship.heartCountAfter}
          </dd>
        </div>
        <div>
          <dt>Bônus</dt>
          <dd data-testid="primordial-summary-bonus">
            +{ship.bonusPercent}% → +{ship.bonusPercentAfter}%
          </dd>
        </div>
      </dl>
      {/*
        A razão da recusa fica JUNTO da nave a que ela pertence, e o bloqueio
        actualizado aparece aqui também: a lista foi relida, e se esta nave
        entretanto foi anunciada é aqui que o jogador tem de o ver.
      */}
      {ship.blockText ? (
        <p className="primordial-ship-block" data-testid="primordial-summary-block">
          {ship.blockText}
        </p>
      ) : null}
      {failure ? (
        <p className="primordial-failure" role="alert" data-testid="primordial-failure">
          {failure}
        </p>
      ) : null}
      <label className="primordial-ack" htmlFor={ackId}>
        <input
          id={ackId}
          type="checkbox"
          data-testid="primordial-ack"
          checked={acknowledged}
          disabled={busy}
          onChange={(event) => onAcknowledge(event.currentTarget.checked)}
        />
        <span>{INCORPORATION_ACKNOWLEDGEMENT}</span>
      </label>
      <div className="primordial-actions">
        {/*
          Os dois botões pesam o mesmo.
          Cancelar não é apagado nem pequeno: a acção que se está a oferecer é
          irreversível, e esconder a saída seria empurrar para ela.
        */}
        <button
          type="button"
          className="primordial-button"
          data-weight="equal"
          data-testid="primordial-cancel"
          disabled={busy}
          onClick={onBack}
        >
          VOLTAR SEM INCORPORAR
        </button>
        <button
          type="button"
          className="primordial-button"
          data-weight="equal"
          data-tone="danger"
          data-testid="primordial-confirm"
          /*
           * Bloqueado sem o checkbox E durante o pedido.
           *
           * O segundo é o que faz um duplo clique ser uma operação: o botão
           * fecha no primeiro, e a chave de idempotência de quem chama é a
           * segunda linha de defesa, não a primeira.
           */
          disabled={!acknowledged || busy || !ship.eligible}
          onClick={onConfirm}
        >
          {busy ? 'INCORPORANDO…' : 'INCORPORAR PERMANENTEMENTE'}
        </button>
      </div>
      {busy ? (
        <p className="primordial-busy" role="status" data-testid="primordial-busy">
          Incorporando… não feche esta janela.
        </p>
      ) : null}
    </>
  );
}

/* -------------------------------------------------------------- sucesso -- */

function SuccessPanel({
  titleId,
  confirmed,
  onClose,
}: {
  titleId: string;
  confirmed: Confirmed;
  onClose: () => void;
}) {
  return (
    <>
      <h2 className="primordial-title" id={titleId}>
        CORAÇÃO INCORPORADO
      </h2>
      <p className="primordial-lead" role="status" data-testid="primordial-success">
        {confirmed.name} agora carrega {confirmed.after}{' '}
        {confirmed.after === '1' ? 'Coração Primordial' : 'Corações Primordiais'}.
      </p>
      <dl className="primordial-summary">
        <div>
          <dt>Corações</dt>
          <dd data-testid="primordial-success-hearts">
            {confirmed.before} → {confirmed.after}
          </dd>
        </div>
        <div>
          <dt>Bônus</dt>
          <dd data-testid="primordial-success-bonus">
            +{confirmed.bonusBefore}% → +{confirmed.bonusAfter}%
          </dd>
        </div>
      </dl>
      <div className="primordial-actions">
        <button
          type="button"
          className="primordial-button"
          data-weight="equal"
          data-testid="primordial-close"
          onClick={onClose}
        >
          FECHAR
        </button>
      </div>
    </>
  );
}
